"use client";

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button} from "@root/server/client/esweb/components/ui/button";
import {cn} from "@root/server/client/esweb/lib/utils";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController";
import {Context} from "@hotwired/stimulus";
import {t} from "@root/server/client/esweb/src/lib/i18n.ts";
import {subscribe} from "@root/server/client/esweb/src/lib/ws.ts";

import Debug from 'debug';
import {getClientConfig} from "@root/server/client/esweb/src/lib/config.ts";

export const name = 'lib--upload';

const debug = Debug(`app:${name}`);

const config = getClientConfig();

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => (
            <RobustUpload
                headers={{"x-csrf-token": config.CSRF_TOKEN}}
                accept="image/*"
                responseType="json"
                {...props as RobustUploadProps}
            />
        ));
    }
}

const generateId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

type UploadStatus = "uploading" | "success" | "error" | "aborted";

interface UploadItem {
    id: string;
    file: File;
    progress: number;
    status: UploadStatus;
    error?: string;
    response?: unknown;
}

export interface UploadCompletePayload {
    // file: File;
    // status: number;
    response: unknown;
    // xhr: XMLHttpRequest;
}

export interface UploadErrorPayload {
    file: File;
    status: number;
    error: string;
    xhr: XMLHttpRequest;
}

export interface RobustUploadProps {
    action: string;
    method?: "POST" | "PUT" | "PATCH";
    headers?: Record<string, string>;
    fieldName?: string;
    additionalData?: Record<string, string | Blob>;
    multiple?: boolean;
    accept?: string;
    withCredentials?: boolean;
    responseType?: XMLHttpRequestResponseType;
    dropLabel?: React.ReactNode;
    className?: string;
    onComplete?: (payload: UploadCompletePayload) => void;
    onError?: (payload: UploadErrorPayload) => void;
    onUploadingChange?: (isUploading: boolean) => void;
    signedChannel?: string;
}

export function RobustUpload({
    action,
    method = "POST",
    headers,
    fieldName = "file",
    additionalData,
    multiple = true,
    accept,
    withCredentials = false,
    responseType,
    dropLabel,
    className,
    onComplete,
    onError,
    onUploadingChange,
    signedChannel,
}: RobustUploadProps) {
    const [uploads, setUploads] = useState<UploadItem[]>([]);
    const [isDocumentDragging, setIsDocumentDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const cameraInputRef = useRef<HTMLInputElement | null>(null);
    const requestsRef = useRef<Map<string, XMLHttpRequest>>(new Map());
    const dragCounterRef = useRef(0);

    const mergedHeaders = useMemo(() => headers ?? {}, [headers]);

    useEffect(() => {
        if(signedChannel) {
            return subscribe(signedChannel, ({detail}) => {
                debug('received', detail);
                onComplete?.({response: detail.data});
            })
        }
    }, [])

    const updateUpload = useCallback((id: string, updater: (item: UploadItem) => UploadItem) => {
        setUploads((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
    }, []);

    const startUpload = useCallback((file: File) => {
        const id = generateId();

        const xhr = new XMLHttpRequest();
        xhr.open(method, action);

        if (responseType) {
            xhr.responseType = responseType;
        }

        xhr.withCredentials = withCredentials;

        Object.entries(mergedHeaders).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                xhr.setRequestHeader(key, value);
            }
        });

        xhr.upload.addEventListener("progress", (event) => {
            if (!event.lengthComputable) {
                return;
            }
            const percent = Math.round((event.loaded / event.total) * 100);
            updateUpload(id, (item) => ({...item, progress: percent}));
        });

        xhr.addEventListener("load", () => {
            requestsRef.current.delete(id);
            const success = xhr.status >= 200 && xhr.status < 300;
            updateUpload(id, (item) => ({
                ...item,
                progress: 100,
                status: success ? "success" : "error",
                error: success ? undefined : xhr.statusText || `Upload failed (${xhr.status})`,
                response: xhr.response,
            }));

            if (success) {
                setUploads((prev) => prev.filter((item) => item.id !== id));
            }

            if (success) {
                // onComplete?.({file, status: xhr.status, response: xhr.response, xhr});
                onComplete?.({response: xhr.response});
            } else {
                onError?.({
                    file,
                    status: xhr.status,
                    error: xhr.statusText || `Upload failed (${xhr.status})`,
                    xhr,
                });
            }
        });

        xhr.addEventListener("error", () => {
            requestsRef.current.delete(id);
            const errorMessage = xhr.statusText || "Network error";
            updateUpload(id, (item) => ({...item, status: "error", error: errorMessage}));
            onError?.({file, status: xhr.status || 0, error: errorMessage, xhr});
        });

        xhr.addEventListener("abort", () => {
            requestsRef.current.delete(id);
            updateUpload(id, (item) => ({...item, status: "aborted", error: undefined}));
        });

        const formData = new FormData();
        formData.append(fieldName, file, file.name);

        if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }

        setUploads((prev) => [
            ...prev,
            {
                id,
                file,
                progress: 0,
                status: "uploading",
            },
        ]);

        requestsRef.current.set(id, xhr);
        xhr.send(formData);
    }, [action, additionalData, fieldName, mergedHeaders, method, onComplete, onError, responseType, updateUpload, withCredentials]);

    const handleFiles = useCallback((files: FileList | File[]) => {
        const fileArray = Array.from(files);
        if (!fileArray.length) {
            return;
        }

        const selection = multiple ? fileArray : [fileArray[0]];
        selection.forEach((file) => startUpload(file));
    }, [multiple, startUpload]);

    useEffect(() => {
        const handleWindowDragEnter = (event: DragEvent) => {
            event.preventDefault();
            dragCounterRef.current += 1;
            setIsDocumentDragging(true);
        };

        const handleWindowDragOver = (event: DragEvent) => {
            event.preventDefault();
        };

        const handleWindowDragLeave = (event: DragEvent) => {
            event.preventDefault();
            dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
            if (dragCounterRef.current === 0) {
                setIsDocumentDragging(false);
            }
        };

        const handleWindowDrop = (event: DragEvent) => {
            event.preventDefault();
            dragCounterRef.current = 0;
            setIsDocumentDragging(false);
            if (event.dataTransfer?.files?.length) {
                handleFiles(event.dataTransfer.files);
            }
        };

        window.addEventListener("dragenter", handleWindowDragEnter);
        window.addEventListener("dragover", handleWindowDragOver);
        window.addEventListener("dragleave", handleWindowDragLeave);
        window.addEventListener("drop", handleWindowDrop);

        return () => {
            window.removeEventListener("dragenter", handleWindowDragEnter);
            window.removeEventListener("dragover", handleWindowDragOver);
            window.removeEventListener("dragleave", handleWindowDragLeave);
            window.removeEventListener("drop", handleWindowDrop);
        };
    }, [handleFiles]);

    const isUploading = useMemo(() => uploads.some((upload) => upload.status === "uploading"), [uploads]);

    useEffect(() => {
        onUploadingChange?.(isUploading);
    }, [isUploading, onUploadingChange]);

    useEffect(() => {
        return () => {
            requestsRef.current.forEach((xhr) => {
                try {
                    xhr.abort();
                } catch (error) {
                    console.error("Failed to abort request", error);
                }
            });
            requestsRef.current.clear();
        };
    }, []);

    const onDropAreaDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const onDropAreaDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragCounterRef.current = 0;
        setIsDocumentDragging(false);
        if (event.dataTransfer?.files?.length) {
            handleFiles(event.dataTransfer.files);
        }
    }, [handleFiles]);

    const openFileDialog = useCallback(() => {
        inputRef.current?.click();
    }, []);

    const openCameraDialog = useCallback(() => {
        cameraInputRef.current?.click();
    }, []);

    const abortUpload = useCallback((id: string) => {
        const request = requestsRef.current.get(id);
        if (request) {
            request.abort();
            requestsRef.current.delete(id);
        }
        updateUpload(id, (item) => ({...item, status: "aborted"}));
    }, [updateUpload]);

    const renderDropLabel = useMemo(() => {
        if (dropLabel) {
            return dropLabel;
        }

        return (
            <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">Přetáhněte soubory sem</p>
                <p className="text-xs text-muted-foreground">nebo klikněte pro výběr (podporováno i přetažení kamkoliv na stránku)</p>
            </div>
        );
    }, [dropLabel]);

    return (
        <div className={cn("flex flex-col gap-6", className)}>
            <div
                onDragOver={onDropAreaDragOver}
                onDrop={onDropAreaDrop}
                onClick={openFileDialog}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openFileDialog();
                    }
                }}
                className={cn(
                    "relative flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-10 transition-colors",
                    "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                    isDocumentDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-muted-foreground/50"
                )}
            >
                {renderDropLabel}
                <Button type="button" variant="outline" className="pointer-events-none">
                    {t({cs: 'Vybrat soubory', en: 'Select files'})}
                </Button>
                <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    multiple={multiple}
                    accept={accept}
                    onChange={(event) => {
                        if (event.target.files) {
                            handleFiles(event.target.files);
                            event.target.value = "";
                        }
                    }}
                />
            </div>

            <div className="md:hidden">
                <Button
                    type="button"
                    className="w-full"
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        openCameraDialog();
                    }}
                >
                    Vyfotit mobilem
                </Button>
                <input
                    ref={cameraInputRef}
                    type="file"
                    className="hidden"
                    accept={accept ?? "image/*"}
                    capture="environment"
                    onChange={(event) => {
                        if (event.target.files) {
                            handleFiles(event.target.files);
                            event.target.value = "";
                        }
                    }}
                />
            </div>

            {uploads.length > 0 && (
                <div className="space-y-4">
                    {uploads.map((upload) => (
                        <div
                            key={upload.id}
                            className="rounded-lg border bg-card p-4 shadow-sm"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                    <p className="text-sm font-medium text-foreground">{upload.file.name}</p>
                                    <p className="text-xs text-muted-foreground">{formatFileSize(upload.file.size)}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs uppercase tracking-wide text-muted-foreground">
                                        {statusLabel(upload.status)}
                                    </span>
                                    {upload.status === "uploading" && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => abortUpload(upload.id)}
                                        >
                                            Zrušit
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="mt-3 h-2 w-full rounded-full bg-muted">
                                <div
                                    className={cn(
                                        "h-full rounded-full transition-all",
                                        upload.status === "error" ? "bg-destructive" : "bg-primary"
                                    )}
                                    style={{width: `${upload.progress}%`}}
                                />
                            </div>

                            {upload.error && (
                                <p className="mt-2 text-xs text-destructive">{upload.error}</p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function formatFileSize(size: number) {
    const units = ["B", "KB", "MB", "GB", "TB"];
    let index = 0;
    let value = size;
    while (value >= 1024 && index < units.length - 1) {
        value /= 1024;
        index += 1;
    }
    return `${value.toFixed(value < 10 && index > 0 ? 1 : 0)} ${units[index]}`;
}

function statusLabel(status: UploadStatus) {
    switch (status) {
        case "uploading":
            return "Probíhá";
        case "success":
            return "Hotovo";
        case "error":
            return "Chyba";
        case "aborted":
            return "Zrušeno";
        default:
            return "";
    }
}
